import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { portalFilenamify } from './portalFilenamify';

dayjs.extend(customParseFormat);
dayjs.extend(quarterOfYear);

export const DOCVIEW_FILE_NAME_DATE_FORMATS = [
  'YYMMDD',
  'DDMMYYYY',
  'Q YYYY',
] as const;

export type DocviewFileNameDateFormat =
  (typeof DOCVIEW_FILE_NAME_DATE_FORMATS)[number];

export type DocviewFileNamePatternSource = 'value' | 'label';

export type DocviewFileNamePatternPlaceholder = {
  raw: string;
  prefix: string;
  key: string;
  source: DocviewFileNamePatternSource;
  format?: string;
  suffix: string;
};

type ParsedDocviewFileNamePatternPlaceholder =
  DocviewFileNamePatternPlaceholder & {
    start: number;
    end: number;
  };

type ParsedDocviewFileNamePatternValidationIssue =
  DocviewFileNamePatternValidationIssue & {
    start: number;
  };

export type DocviewFileNamePatternMetadata = {
  key: string;
  isDateMetadata: boolean;
};

export type DocviewFileNamePatternValidationIssue =
  | { code: 'unknown-key'; key: string }
  | { code: 'unsupported-format'; key: string; format: string }
  | { code: 'format-on-non-date'; key: string; format: string }
  | { code: 'malformed-placeholder'; value: string };

export type DocviewFileNamePatternValueResolver = (
  placeholder: DocviewFileNamePatternPlaceholder
) => {
  value: string | null;
  isDateMetadata: boolean;
};

type ApplyDocviewFileNamePatternOptions = {
  resolveValue: DocviewFileNamePatternValueResolver;
  onUnresolved?: 'preserve' | 'omit';
  trim?: boolean;
  sanitize?: boolean;
};

const PLACEHOLDER_START = '{{';
const PLACEHOLDER_END = '}}';
const PLACEHOLDER_BODY_PATTERN =
  /^(?<prefix>[^{}]*)\$(?<key>\w+):(?<source>value|label)(?<tail>[^{}]*)$/;

export function isSupportedDocviewFileNameDateFormat(
  format: string
): format is DocviewFileNameDateFormat {
  return (DOCVIEW_FILE_NAME_DATE_FORMATS as readonly string[]).includes(format);
}

/**
 * A format segment is recognized only when a known date format is followed by
 * a non-alphanumeric character or the end of the tail. Unsupported tokens
 * (e.g. :DD, :YYYYMMDD) stay in the suffix so validation can reject them.
 */
function findSupportedFormat(
  tail: string
): DocviewFileNameDateFormat | undefined {
  return DOCVIEW_FILE_NAME_DATE_FORMATS.find((format) => {
    if (!tail.startsWith(`:${format}`)) {
      return false;
    }

    const charAfterFormat = tail.charAt(format.length + 1);
    return charAfterFormat === '' || !/[A-Za-z0-9]/.test(charAfterFormat);
  });
}

function getFormatToken(tail: string): string | undefined {
  if (!tail.startsWith(':')) {
    return undefined;
  }

  return tail.slice(1).split(':')[0];
}

function parsePlaceholderBody(
  raw: string,
  body: string,
  start: number,
  end: number
): ParsedDocviewFileNamePatternPlaceholder | null {
  const match = PLACEHOLDER_BODY_PATTERN.exec(body);
  if (!match?.groups) {
    return null;
  }

  const { prefix, key, source, tail } = match.groups;
  const supportedFormat = findSupportedFormat(tail);

  return {
    raw,
    prefix,
    key,
    source: source as DocviewFileNamePatternSource,
    format: supportedFormat,
    suffix: supportedFormat ? tail.slice(supportedFormat.length + 1) : tail,
    start,
    end,
  };
}

function parseDocviewFileNamePattern(pattern: string): {
  placeholders: ParsedDocviewFileNamePatternPlaceholder[];
  issues: ParsedDocviewFileNamePatternValidationIssue[];
} {
  const placeholders: ParsedDocviewFileNamePatternPlaceholder[] = [];
  const issues: ParsedDocviewFileNamePatternValidationIssue[] = [];
  let searchIndex = 0;

  while (searchIndex < pattern.length) {
    const start = pattern.indexOf(PLACEHOLDER_START, searchIndex);
    if (start === -1) {
      break;
    }

    const end = pattern.indexOf(PLACEHOLDER_END, start + 2);
    if (end === -1) {
      issues.push({
        code: 'malformed-placeholder',
        start,
        value: pattern.slice(start),
      });
      break;
    }

    const raw = pattern.slice(start, end + 2);
    const body = pattern.slice(start + 2, end);
    const placeholder = parsePlaceholderBody(raw, body, start, end + 2);
    if (placeholder) {
      placeholders.push(placeholder);
    } else {
      issues.push({ code: 'malformed-placeholder', start, value: raw });
    }

    searchIndex = end + 2;
  }

  return { placeholders, issues };
}

export function validateDocviewFileNamePattern(
  pattern: string,
  metadata: DocviewFileNamePatternMetadata[]
): DocviewFileNamePatternValidationIssue[] {
  const metadataByKey = new Map(
    metadata.map((item) => [item.key, item.isDateMetadata])
  );
  const { placeholders, issues } = parseDocviewFileNamePattern(pattern);

  for (const placeholder of placeholders) {
    const isDateMetadata = metadataByKey.get(placeholder.key);
    if (isDateMetadata == null) {
      issues.push({
        code: 'unknown-key',
        key: placeholder.key,
        start: placeholder.start,
      });
      continue;
    }

    const formatToken =
      placeholder.format ?? getFormatToken(placeholder.suffix);
    if (!formatToken) {
      continue;
    }

    if (!isDateMetadata) {
      issues.push({
        code: 'format-on-non-date',
        key: placeholder.key,
        format: formatToken,
        start: placeholder.start,
      });
      continue;
    }

    if (!isSupportedDocviewFileNameDateFormat(formatToken)) {
      issues.push({
        code: 'unsupported-format',
        key: placeholder.key,
        format: formatToken,
        start: placeholder.start,
      });
    }
  }

  return issues
    .sort((left, right) => left.start - right.start)
    .map(({ start: _start, ...issue }) => issue);
}

export function applyDocviewFileNamePattern(
  pattern: string,
  {
    resolveValue,
    onUnresolved = 'preserve',
    sanitize = false,
    trim = false,
  }: ApplyDocviewFileNamePatternOptions
): string {
  const { placeholders } = parseDocviewFileNamePattern(pattern);
  let result = '';
  let currentIndex = 0;

  for (const placeholder of placeholders) {
    result += pattern.slice(currentIndex, placeholder.start);
    const { value, isDateMetadata } = resolveValue(placeholder);

    if (value) {
      result += buildDocviewFileNamePlaceholderSegment(
        placeholder.prefix,
        value,
        placeholder.format,
        placeholder.suffix,
        isDateMetadata
      );
    } else if (onUnresolved === 'preserve') {
      result += placeholder.raw;
    }

    currentIndex = placeholder.end;
  }

  result += pattern.slice(currentIndex);

  if (trim) {
    result = result.trim();
  }

  if (!result) {
    return result;
  }

  return sanitize ? portalFilenamify(result) : result;
}

/** Same calendar-date parse DateWidget uses when storing upload metadata values. */
function parseDateOnly(value: string): Dayjs | null {
  const date = dayjs(value.trim(), 'YYYY-MM-DD', true);
  return date.isValid() ? date : null;
}

function formatQYYYY(date: Dayjs): string {
  if (date.isSame(date.endOf('quarter'), 'day')) {
    return `Q${date.quarter()} ${date.year()}`;
  }

  return date.format('DDMMYYYY');
}

function formatParsedDate(
  date: Dayjs,
  format: DocviewFileNameDateFormat
): string {
  switch (format) {
    case 'YYMMDD':
      return date.format('YYMMDD');
    case 'DDMMYYYY':
      return date.format('DDMMYYYY');
    case 'Q YYYY':
      return formatQYYYY(date);
    default:
      return date.format('YYYY-MM-DD');
  }
}

export function isJsonSchemaDateProperty(
  property: { format?: string } | boolean | undefined
): boolean {
  if (!property || typeof property !== 'object') {
    return false;
  }

  // `datetime` is emitted by gateway jsonSchemaHelpers for datetime metadata;
  // `date-time` is the JSON Schema / OpenAPI spelling — accept both.
  return (
    property.format != null &&
    ['date', 'date-time', 'datetime'].includes(property.format)
  );
}

/**
 * Builds the replaced placeholder segment. Date formats are applied only when
 * the key is date metadata, the format is supported, and the resolved value
 * parses as YYYY-MM-DD. Otherwise a captured format (and any trailing suffix)
 * is kept as literal text — including on non-date keys.
 */
export function buildDocviewFileNamePlaceholderSegment(
  prefix: string,
  resolvedValue: string,
  format: string | undefined,
  suffix: string,
  isDateMetadata: boolean
): string {
  if (
    typeof format === 'string' &&
    isDateMetadata &&
    isSupportedDocviewFileNameDateFormat(format)
  ) {
    const parsedDate = parseDateOnly(resolvedValue);
    if (parsedDate) {
      return `${prefix}${formatParsedDate(parsedDate, format)}${suffix}`;
    }
  }

  const literalFormat = format ? `:${format}` : '';
  return `${prefix}${resolvedValue}${literalFormat}${suffix}`;
}
